import { useQuery } from "@tanstack/react-query";
import { produce } from "immer";

import { GraphQLAPI, GraphQLResult, queries, typedGql } from "@examspark/api";
import { collectAllPages, exists } from "@examspark/common";
import {
  GenericReferenceId,
  ParsedTCMPCUnit,
  SearchableResourceConnectionNew,
  Vocabulary,
  SearchableResourceFilterInput,
} from "@examspark/types";

import { useAuthorizationContext } from "../../hooks/useAuthorizationContext";

export const useQueryGetVocabulary = (
  vocabularyId: string | undefined,
  vocabularySk: string,
) => {
  const authorizationContext = useAuthorizationContext();

  return useQuery({
    queryKey: ["vocabulary", vocabularyId],
    queryFn: async () => {
      if (vocabularyId == null) return null;

      const query = typedGql(queries.vocabulary.getVocabularySimple);

      const vocabulary = await query({
        input: {
          id: vocabularyId,
          sk: vocabularySk,
        },
      });

      const usagesFilter: SearchableResourceFilterInput = {
        referencesIds: {
          eq: vocabularyId,
        },
        sk: {
          eq: "unit#v0",
        },
      };

      const usages = await collectAllPages(async (nextToken) => {
        const result = (await GraphQLAPI.query({
          query: queries.search.search({
            fields: `
              id 
              sk 
              tags { key value }
            `,
          }),
          variables: {
            authorizationContext,
            filter: usagesFilter,
            nextToken,
          },
        })) as GraphQLResult<{ search: SearchableResourceConnectionNew }>;

        return result.data?.search;
      });

      const units = (
        await Promise.all(
          usages.flatMap((page) =>
            page.items?.map((item) => {
              return typedGql(queries.unit.getUnitSimple)({
                input: {
                  id: item.id,
                  sk: item.sk,
                },
              });
            }),
          ),
        )
      )
        .map((result) => result?.data?.getUnit)
        .filter(exists);

      return produce(vocabulary.data?.getVocabulary, (draft) => {
        if (!draft) return undefined;

        const ret: Vocabulary = {
          ...draft,
          usages: {
            count: units.length,
            items: units.map((unit) => ({
              id: unit.id,
              sk: unit.sk,
              tags: [],
              item: unit,
            })),
          },
        };

        return ret;
      });
    },
  });
};

export const useQueryGetUnitVocabulary = (
  unit: ParsedTCMPCUnit | null | undefined,
) => {
  const authorizationContext = useAuthorizationContext();
  return useQuery({
    queryKey: ["unit", unit?.id, "vocabulary"],
    enabled: !!unit?.id,
    queryFn: async () => {
      const chunkedVocab: GenericReferenceId[][] = [];

      if (!unit?.vocabulariesIds) return [];

      for (let i = 0; i < unit?.vocabulariesIds?.length; i += 100) {
        chunkedVocab.push(unit?.vocabulariesIds?.slice(i, i + 100));
      }

      const pages = await Promise.all(
        chunkedVocab.map(async (vocabChunk) => {
          const result = await typedGql(queries.unit.resolveUnitVocabularies)({
            input: vocabChunk,
            authorizationContext,
          });

          return result.data?.resolveUnitVocabularies.items ?? [];
        }),
      );

      return pages.flat();
    },
  });
};
